import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';
import type { ComparedValueRow } from '../checkpointComparedValues';
import { comparedValuesForCheckpointStep } from '../checkpointComparedValues';
import { buildAiDigitalFootprintEvidenceView } from './aiDigitalFootprintPresentation';
import type { ReviewReasonEvidence } from './reviewReason.types';

export type LegitimacyCheckpointView = {
  popoverTitle: string;
  dataPoints: ReviewReasonEvidence[];
  secondaryDataPoints?: ReviewReasonEvidence[];
  secondarySectionLabel?: string;
  issueStatement: string;
  issueSeverity: 'warning' | 'error' | 'info';
  issueLabel: string;
  footerNote: string | null;
  advisoryLabel: string | null;
  advisorySeverity: 'warning' | 'success' | 'default';
};

export const LEGITIMACY_CHECKPOINT_STEPS = [
  'DIGITAL_FOOTPRINT',
  'OPERATIONAL_STATUS',
  'PLACE_PHOTOS_VISION',
  'SATELLITE_BUILDING_VISION',
  'AI_DIGITAL_FOOTPRINT',
  'WEB_DOMAIN',
  'ACTIVE_DIRECTORS',
  'PUBLIC_HIRING_ACTIVITY',
] as const;

export type LegitimacyCheckpointStep = (typeof LEGITIMACY_CHECKPOINT_STEPS)[number];

const STEP_POPOVER_TITLES: Record<LegitimacyCheckpointStep, string> = {
  DIGITAL_FOOTPRINT: 'Google Places footprint',
  OPERATIONAL_STATUS: 'Business operational status',
  PLACE_PHOTOS_VISION: 'Maps listing photos',
  SATELLITE_BUILDING_VISION: 'Satellite building check',
  AI_DIGITAL_FOOTPRINT: 'AI public footprint',
  WEB_DOMAIN: 'Employer website domain',
  ACTIVE_DIRECTORS: 'Corporate directors',
  PUBLIC_HIRING_ACTIVITY: 'Public hiring activity',
};

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function pushPoint(
  rows: ReviewReasonEvidence[],
  label: string,
  value: string | null | undefined,
  tone: ReviewReasonEvidence['tone'],
) {
  if (value) rows.push({ label, value, tone });
}

function normalizeStep(step: string): LegitimacyCheckpointStep | null {
  const u = step.trim().toUpperCase();
  return (LEGITIMACY_CHECKPOINT_STEPS as readonly string[]).includes(u)
    ? (u as LegitimacyCheckpointStep)
    : null;
}

export function isLegitimacyCheckpoint(step: string): boolean {
  return normalizeStep(step) != null;
}

function oneSentence(text: string, maxLen = 240): string {
  const cleaned = text.replace(/\s{2,}/g, ' ').trim();
  if (!cleaned) return '';
  if (cleaned.length <= maxLen) return cleaned.endsWith('.') ? cleaned : `${cleaned}.`;
  return `${cleaned.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

function bestIssueSentence(text: string): string {
  const cleaned = text.replace(/\s{2,}/g, ' ').trim();
  if (!cleaned) return '';
  const sentences = cleaned.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
  const attention = sentences.find((s) =>
    /does not|exceeds|could not|marked for review|below threshold|contradict|unrealistic|missing|no active|expired|inconclusive|needs review|not confirm|not align|not fit|attention:/i.test(
      s,
    ),
  );
  if (attention) return oneSentence(attention);
  if (sentences.length > 1) return oneSentence(sentences[sentences.length - 1]!);
  return oneSentence(cleaned);
}

function issueMetaFromBucket(bucket: string): {
  severity: LegitimacyCheckpointView['issueSeverity'];
  label: string;
} {
  const u = bucket.trim().toLowerCase();
  if (u === 'fail') return { severity: 'error', label: 'Issue' };
  if (u === 'pass') return { severity: 'info', label: 'Outcome' };
  return { severity: 'warning', label: 'Issue' };
}

function comparedMap(compared: ComparedValueRow[]): Map<string, string> {
  return new Map(compared.map((row) => [row.label, row.value]));
}

function humanizeCategory(value: string | null): string | null {
  if (!value) return null;
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function parseRatingFromReason(reason: string): { rating: string | null; reviews: string | null } {
  const rating = reason.match(/shows\s+(\d+(?:\.\d+)?)\/5/i)?.[1] ?? null;
  const reviews = reason.match(/from\s+(\d+)\s+reviews/i)?.[1] ?? null;
  return { rating, reviews };
}

function parseMediaFromReason(reason: string): string | null {
  const company = reason.match(/Found\s+(\d+)\s+company\s+images?/i)?.[1];
  const satellite = reason.match(/(\d+)\s+satellite\s+images?\s+of\s+the\s+employer\s+address/i)?.[1];
  if (company == null && satellite == null) return null;
  const parts: string[] = [];
  if (company != null) parts.push(`${company} listing photo${company === '1' ? '' : 's'}`);
  if (satellite != null) parts.push(`${satellite} satellite image${satellite === '1' ? '' : 's'}`);
  return parts.join(' · ');
}

function parseVisionFromReason(reason: string): { label: string | null; category: string | null } {
  const label = reason.match(/vision:\s*([^(]+)/i)?.[1]?.trim() ?? null;
  const category = reason.match(/\(([^)]+)\)/)?.[1]?.trim() ?? null;
  return { label, category };
}

function simplifyLegitimacyIssue(step: LegitimacyCheckpointStep, reason: string, bucket: string): string {
  const status = bucket.trim().toLowerCase();
  const r = reason.trim();

  switch (step) {
    case 'DIGITAL_FOOTPRINT': {
      const { rating, reviews } = parseRatingFromReason(r);
      const gap = r.match(/Gap:\s*([^.]+)/i)?.[1]?.trim();
      if (/no Google Places footprint|no rating and 0 reviews/i.test(r)) {
        return 'No Google Places rating or reviews were found for this employer.';
      }
      if (gap && status !== 'pass') {
        return `Google Places footprint is below pass thresholds (${gap}).`;
      }
      if (rating && reviews && status !== 'pass') {
        return `Google Places shows ${rating}/5 from ${reviews} reviews — below the minimum rating and review volume.`;
      }
      if (rating && reviews) {
        return `Google Places shows ${rating}/5 from ${reviews} reviews — footprint meets pass thresholds.`;
      }
      break;
    }
    case 'OPERATIONAL_STATUS': {
      const got = r.match(/Google Places status is\s+([A-Z_]+)/i)?.[1];
      const expected = r.match(/Expected\s+([A-Z_]+)/i)?.[1] ?? 'OPERATIONAL';
      if (/did not return an operational status/i.test(r)) {
        return `Google Places did not return a business status — expected ${expected}.`;
      }
      if (got && status !== 'pass') {
        return `Google Places status is ${got}; expected ${expected}.`;
      }
      if (got) return `Google Places reports the business as ${got}.`;
      break;
    }
    case 'PLACE_PHOTOS_VISION': {
      const { label, category } = parseVisionFromReason(r);
      if (/contradict|marked for review|needs review/i.test(r)) {
        return category
          ? `Maps listing photos suggest a contradiction (${humanizeCategory(category)}).`
          : 'Maps listing photos suggest a workplace contradiction.';
      }
      if (label) return `Maps listing photos: ${label.replace(/\.$/, '')}.`;
      break;
    }
    case 'SATELLITE_BUILDING_VISION': {
      const { label, category } = parseVisionFromReason(r);
      if (/Empty land|Open water|Residential|marked for review|needs review/i.test(r)) {
        return category
          ? `Satellite imagery raises a concern (${humanizeCategory(category)}).`
          : 'Satellite imagery raises a concern about the workplace building.';
      }
      if (label) return `Satellite imagery: ${label.replace(/\.$/, '')}.`;
      break;
    }
    case 'AI_DIGITAL_FOOTPRINT': {
      const name = r.match(/\):\s*([^.]+)\./)?.[1]?.trim();
      const conf = r.match(/confidence\s+(\d+(?:\.\d+)?)/i)?.[1];
      if (name && conf) return `AI public footprint matched ${name} with ${conf} confidence.`;
      if (name) return `AI public footprint matched ${name}.`;
      break;
    }
    case 'WEB_DOMAIN': {
      if (/no website/i.test(r)) return 'AI digital footprint had no website to check.';
      if (/social profile/i.test(r)) return 'The listed website is a social profile, not a company domain.';
      const domain = r.match(/Checked\s+(\S+)/i)?.[1];
      const whois = r.match(/WHOIS status\s+([A-Z]+)/i)?.[1];
      if (domain && whois && /EXPIRED|FAIL/i.test(whois)) {
        return `Domain ${domain} has WHOIS status ${whois}.`;
      }
      if (domain && whois) return `Checked ${domain} — WHOIS status ${whois}.`;
      break;
    }
    case 'ACTIVE_DIRECTORS': {
      if (/Public-sector employer|not applicable/i.test(r)) {
        return 'Director registry check not required for this public-sector employer.';
      }
      if (/AI digital footprint/i.test(r)) {
        return 'Directors were inferred from AI public footprint, not the federal registry.';
      }
      if (/No active directors/i.test(r)) {
        return 'No active directors were found on the federal corporate registry.';
      }
      if (/At least one active registered director/i.test(r)) {
        return 'At least one active registered director is on file.';
      }
      break;
    }
    default:
      break;
  }

  return bestIssueSentence(r);
}

function buildDataPointsForStep(
  step: LegitimacyCheckpointStep,
  input: {
    detail: EmployerVerificationDetail;
    footprint?: EmployerFootprintAdminViewDto | null;
    compared: ComparedValueRow[];
    reason: string;
  },
): ReviewReasonEvidence[] {
  const compared = comparedMap(input.compared);
  const employerName = readString(input.detail.employer?.legalName);
  const rows: ReviewReasonEvidence[] = [];

  switch (step) {
    case 'DIGITAL_FOOTPRINT': {
      const parsed = parseRatingFromReason(input.reason);
      pushPoint(rows, 'Google rating', parsed.rating ? `${parsed.rating}/5` : compared.get('Google rating'), 'reviews');
      pushPoint(rows, 'Review count', parsed.reviews ?? compared.get('Review count'), 'places');
      pushPoint(rows, 'Pass threshold', compared.get('Pass threshold') ?? '≥3★ / ≥5 reviews', 'threshold');
      pushPoint(rows, 'Site media', parseMediaFromReason(input.reason) ?? compared.get('Site media'), 'vision');
      break;
    }
    case 'OPERATIONAL_STATUS':
      pushPoint(rows, 'Google status', compared.get('Google status'), 'places');
      pushPoint(rows, 'Expected status', compared.get('Expected status') ?? 'OPERATIONAL', 'threshold');
      pushPoint(rows, 'Employer', employerName, 'registry');
      pushPoint(rows, 'Data source', compared.get('Data source') ?? 'Google Places', 'aiInsight');
      break;
    case 'PLACE_PHOTOS_VISION': {
      const vision = parseVisionFromReason(input.reason);
      pushPoint(rows, 'Vision result', vision.label ?? compared.get('Vision result'), 'vision');
      pushPoint(
        rows,
        'Category',
        humanizeCategory(vision.category) ?? compared.get('Category'),
        'satellite',
      );
      pushPoint(rows, 'Confidence', compared.get('Confidence'), 'aiInsight');
      pushPoint(rows, 'Media type', compared.get('Media type') ?? 'Maps listing photos', 'places');
      break;
    }
    case 'SATELLITE_BUILDING_VISION': {
      const vision = parseVisionFromReason(input.reason);
      pushPoint(rows, 'Vision result', vision.label ?? compared.get('Vision result'), 'vision');
      pushPoint(
        rows,
        'Category',
        humanizeCategory(vision.category) ?? compared.get('Category'),
        'satellite',
      );
      pushPoint(rows, 'Confidence', compared.get('Confidence'), 'aiInsight');
      pushPoint(rows, 'Media type', compared.get('Media type') ?? 'Satellite imagery', 'places');
      break;
    }
    case 'AI_DIGITAL_FOOTPRINT':
      break;
    case 'WEB_DOMAIN':
      pushPoint(rows, 'Domain', compared.get('Domain'), 'domain');
      pushPoint(rows, 'WHOIS status', compared.get('WHOIS status'), 'registry');
      pushPoint(rows, 'Registrar', compared.get('Registrar'), 'aiInsight');
      pushPoint(rows, 'Registered', compared.get('Registered'), 'threshold');
      break;
    case 'ACTIVE_DIRECTORS':
      pushPoint(rows, 'Active directors', compared.get('Active directors'), 'registry');
      pushPoint(rows, 'Source', compared.get('Source'), 'aiInsight');
      pushPoint(rows, 'Officers cited', compared.get('Officers cited'), 'domain');
      pushPoint(rows, 'Employer', employerName, 'places');
      break;
    case 'PUBLIC_HIRING_ACTIVITY':
      pushPoint(rows, 'Hiring signal', compared.get('Hiring signal'), 'aiInsight');
      pushPoint(rows, 'Postings found', compared.get('Postings found'), 'reviews');
      pushPoint(rows, 'Source', compared.get('Source'), 'places');
      pushPoint(rows, 'Employer', employerName, 'registry');
      break;
  }

  return rows.slice(0, 4);
}

/** OPG-style adjudicator panel for Legitimacy gate checkpoint popovers. */
export function buildLegitimacyCheckpointView(input: {
  step: string;
  reason: string;
  bucket: string;
  detail: EmployerVerificationDetail;
  footprint?: EmployerFootprintAdminViewDto | null;
}): LegitimacyCheckpointView | null {
  const step = normalizeStep(input.step);
  if (!step) return null;

  const evidenceRecord =
    input.detail.evidence != null
      ? (input.detail.evidence as Record<string, unknown>)
      : null;

  if (step === 'AI_DIGITAL_FOOTPRINT') {
    const aiView = buildAiDigitalFootprintEvidenceView({
      evidence: evidenceRecord,
      reason: input.reason,
      bucket: input.bucket,
      declaredEmployer: readString(input.detail.employer?.legalName),
    });
    const meta = issueMetaFromBucket(input.bucket);
    return {
      popoverTitle: STEP_POPOVER_TITLES[step],
      dataPoints: aiView.primaryDataPoints,
      secondaryDataPoints: aiView.collectedSignals,
      secondarySectionLabel: 'Digital analysis collected',
      issueStatement: aiView.issueStatement,
      issueSeverity: meta.severity,
      issueLabel: meta.label,
      footerNote: `${aiView.gateRequirementNote} Advisory public-web signal — does not override registry checks.`,
      advisoryLabel: null,
      advisorySeverity: 'default',
    };
  }

  const compared = evidenceRecord ? comparedValuesForCheckpointStep(step, evidenceRecord) : [];

  const dataPoints = buildDataPointsForStep(step, {
    detail: input.detail,
    footprint: input.footprint,
    compared,
    reason: input.reason,
  });

  const meta = issueMetaFromBucket(input.bucket);

  return {
    popoverTitle: STEP_POPOVER_TITLES[step],
    dataPoints,
    issueStatement: simplifyLegitimacyIssue(step, input.reason, input.bucket),
    issueSeverity: meta.severity,
    issueLabel: meta.label,
    footerNote: null,
    advisoryLabel: null,
    advisorySeverity: 'default',
  };
}
